import { useState, useEffect } from "react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import {
  X, User, Mail, Phone, Award, BarChart3, DollarSign,
  Shield, CheckCircle2, Clock, TrendingUp, Copy, ExternalLink,
  Pencil, Save, ChevronRight, Building2, Globe, FileText, Star, Layers, Zap, Trash2,
  Download, AlertCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import RecordAgreementHistory from "@/components/RecordAgreementHistory";

// ─── Types ────────────────────────────────────────────────────────────────────
interface Rep {
  id: number;
  repCode: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string | null;
  vertical?: string | null;
  agentLevel?: string | null;
  uplineRepCode?: string | null;
  mgaRepCode?: string | null;
  allowedVerticals?: string | null;
  status?: string | null;
  totalLeads?: number;
  totalCommissions?: number;
}

interface AgentDetailDrawerProps {
  rep: Rep | null;
  onClose: () => void;
  onRepUpdated: () => void;
  // Renders a "Delete" action in the header when provided - this component is
  // admin-only (only rendered from AdminDashboard), so no auth gating is
  // needed here beyond prop presence; the actual confirmation dialog and
  // rep.delete mutation already live in AdminDashboard and are reused as-is.
  onDelete?: (repId: number) => void;
}

const ALL_VERTICALS = [
  { key: "Scale360", label: "Scale360", color: "#E8620A", icon: TrendingUp },
  { key: "Fundable360", label: "Fundable360", color: "#84ab4a", icon: FileText },
  { key: "Legacy360", label: "Legacy360", color: "#C8920A", icon: Award },
  { key: "Share360 Business", label: "Share360 (Business)", color: "#1A6FD4", icon: Globe },
  { key: "Share360 Nonprofit", label: "Share360 (Nonprofit)", color: "#0FA8A8", icon: Building2 },
  { key: "Media360", label: "Media360", color: "#7B4FD4", icon: Star },
];

function fmt(n: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
}

export default function AgentDetailDrawer({ rep, onClose, onRepUpdated, onDelete }: AgentDetailDrawerProps) {
  const [activeSection, setActiveSection] = useState<"overview" | "leads" | "commissions" | "agreement" | "edit">("overview");
  const [editForm, setEditForm] = useState<Partial<Rep>>({});
  const [isEditing, setIsEditing] = useState(false);
  const [selectedVerticals, setSelectedVerticals] = useState<string[]>([]);
  const [verticalEditing, setVerticalEditing] = useState(false);
  const [kickstartLoading, setKickstartLoading] = useState(false);
  const [kickstartSent, setKickstartSent] = useState(false);
  const [uplineSyncResult, setUplineSyncResult] = useState<any>(null);

  const utils = trpc.useUtils();

  // The drawer is a stateful singleton reused across every rep the admin
  // opens (no `key` at the call site), so edit state left over from a
  // previously viewed rep must be cleared whenever the `rep` prop changes -
  // otherwise a stale `editForm` from rep A can get saved onto rep B's id.
  useEffect(() => {
    setIsEditing(false);
    setActiveSection("overview");
    setEditForm({});
    setVerticalEditing(false);
  }, [rep?.id]);

  // Queries
  const leadsQuery = trpc.lead.list.useQuery(
    { repCode: rep?.repCode },
    { enabled: !!rep?.repCode }
  );
  const commissionsQuery = trpc.commission.list.useQuery(
    { repCode: rep?.repCode },
    { enabled: !!rep?.repCode }
  );
  const repCodesList = [rep?.uplineRepCode, rep?.mgaRepCode].filter((c): c is string => !!c);
  const namesQuery = trpc.rep.getNamesByCode.useQuery(
    { repCodes: repCodesList },
    { enabled: repCodesList.length > 0 }
  );
  // Signed Platform Participation Agreement for this associate (may be null,
  // or a legacy checkbox acceptance with no document).
  const signedAgreementQuery = trpc.agreement.getSigned.useQuery(
    { repCode: rep?.repCode ?? "" },
    { enabled: !!rep?.repCode }
  );
  const signedAgreement = signedAgreementQuery.data;

  // Upline name sync mutation
  const syncUplineNameMutation = trpc.ghlUplineSync.syncOne.useMutation({
    onSuccess: (data) => {
      setUplineSyncResult(data);
      if (data.skipped) {
        toast.warning(data.reason ?? "Skipped");
      } else if (data.ok) {
        toast.success(`upline_name synced: "${data.newUplineName}"`);
      } else {
        toast.error("Wibiz write failed — check server logs");
      }
    },
    onError: (e) => toast.error(e.message),
  });

  // Mutations
  const editRepMutation = trpc.rep.edit.useMutation({
    onSuccess: () => {
      toast.success("Agent updated successfully");
      utils.rep.leaderboard.invalidate();
      setIsEditing(false);
      onRepUpdated();
    },
    onError: (e) => toast.error(e.message),
  });

  if (!rep) return null;

  // Parse allowed verticals
  let allowedVerticals: string[] | null = null;
  try {
    allowedVerticals = rep.allowedVerticals ? JSON.parse(rep.allowedVerticals as string) : null;
  } catch { allowedVerticals = null; }

  const isVerticalAllowed = (v: string) => allowedVerticals === null || allowedVerticals.includes(v);

  const startEdit = () => {
    setEditForm({
      firstName: rep.firstName,
      lastName: rep.lastName,
      email: rep.email,
      phone: rep.phone ?? "",
      vertical: rep.vertical ?? "",
      agentLevel: rep.agentLevel ?? "Associate",
      uplineRepCode: rep.uplineRepCode ?? "",
      mgaRepCode: rep.mgaRepCode ?? "",
    });
    setSelectedVerticals(allowedVerticals ?? []);
    setIsEditing(true);
  };

  const saveEdit = () => {
    editRepMutation.mutate({
      id: rep.id,
      firstName: editForm.firstName ?? rep.firstName,
      lastName: editForm.lastName ?? rep.lastName,
      email: editForm.email ?? rep.email,
      phone: editForm.phone ?? rep.phone ?? undefined,
      vertical: editForm.vertical ?? rep.vertical ?? undefined,
      agentLevel: (editForm.agentLevel ?? rep.agentLevel ?? "Associate") as "Super Agency" | "Super Team" | "Agency" | "Senior Associate" | "Associate",
      uplineRepCode: editForm.uplineRepCode ?? rep.uplineRepCode ?? undefined,
      mgaRepCode: editForm.mgaRepCode ?? rep.mgaRepCode ?? undefined,
      allowedVerticals: selectedVerticals.length > 0 ? selectedVerticals : null,
    });
  };

  const toggleVertical = (v: string) => {
    setSelectedVerticals(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v]);
  };

  const saveVerticalPermissions = () => {
    editRepMutation.mutate({
      id: rep.id,
      firstName: rep.firstName,
      lastName: rep.lastName,
      email: rep.email,
      allowedVerticals: selectedVerticals.length > 0 ? selectedVerticals : null,
    });
    setVerticalEditing(false);
  };

  const provisionKickstart = async () => {
    setKickstartLoading(true);
    try {
      const res = await fetch("/api/scale360/kickstart/provision", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ repCode: rep.repCode, bypassCert: true }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.error ?? "Provision failed");
        return;
      }
      setKickstartSent(true);
      if (data.skipped) {
        toast.info("Kickstart is not yet configured — credentials saved for when it launches.");
      } else if (data.sent) {
        toast.success(`Kickstart access provisioned for ${rep.firstName}!`);
      } else {
        toast.info("Provision noted — Kickstart will follow up.");
      }
    } catch {
      toast.error("Could not reach provision endpoint.");
    } finally {
      setKickstartLoading(false);
    }
  };

  const leads = leadsQuery.data ?? [];
  const commissions = commissionsQuery.data ?? [];
  const totalComm = commissions.reduce((s: number, c: any) => s + Number(c.amount ?? 0), 0);
  const pendingComm = commissions.filter((c: any) => c.status === "pending").reduce((s: number, c: any) => s + Number(c.amount ?? 0), 0);

  const STAGE_COLORS: Record<string, string> = {
    lead_captured: "#1A6FD4", audit_submitted: "#C8920A", follow_up_active: "#E8620A",
    payment_received: "#0FA8A8", onboarding_submitted: "#7B4FD4", active_client: "#84ab4a", lost_cold: "#D93025",
  };
  const STAGE_LABELS: Record<string, string> = {
    lead_captured: "Lead Captured", audit_submitted: "Audit Submitted", follow_up_active: "Follow-Up Active",
    payment_received: "Payment Received", onboarding_submitted: "Onboarding Submitted", active_client: "Active Client", lost_cold: "Lost / Cold",
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed right-0 top-0 bottom-0 w-full max-w-xl bg-white shadow-2xl z-50 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-[#0D1E35] px-6 py-5 flex items-start justify-between flex-shrink-0">
          <div className="flex items-center gap-4 min-w-0 flex-1">
            <div className="w-12 h-12 rounded-xl bg-[#84ab4a]/15 border border-[#84ab4a]/30 flex items-center justify-center flex-shrink-0">
              <User className="w-6 h-6 text-[#84ab4a]" />
            </div>
            <div className="min-w-0">
              <div className="text-lg font-bold text-white truncate">{rep.firstName} {rep.lastName}</div>
              <div className="flex flex-wrap items-center gap-2 mt-0.5">
                <span className="text-xs font-mono bg-[#84ab4a]/20 text-[#84ab4a] px-2 py-0.5 rounded-full font-bold">{rep.repCode}</span>
                {rep.agentLevel && (
                  <span className="text-xs text-white/50">{rep.agentLevel}</span>
                )}
                <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${rep.status === "active" ? "bg-green-500/20 text-green-400" : "bg-gray-500/20 text-gray-400"}`}>
                  {rep.status ?? "active"}
                </span>
                {/* Agreement status at a glance, without opening a tab. This is
                    the fix for "the agreement is so hard to navigate to" -
                    clicking it jumps straight to the full detail. */}
                <button
                  onClick={() => setActiveSection("agreement")}
                  className={`text-xs px-2 py-0.5 rounded-full font-semibold flex items-center gap-1 transition-colors ${
                    signedAgreementQuery.isLoading
                      ? "bg-white/10 text-white/40"
                      : signedAgreement?.isSigned
                        ? "bg-green-500/20 text-green-400 hover:bg-green-500/30"
                        : "bg-amber-500/20 text-amber-400 hover:bg-amber-500/30"
                  }`}
                >
                  {signedAgreementQuery.isLoading ? (
                    "Agreement…"
                  ) : signedAgreement?.isSigned ? (
                    <><CheckCircle2 className="w-3 h-3" /> Signed</>
                  ) : (
                    <><AlertCircle className="w-3 h-3" /> Agreement</>
                  )}
                </button>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {onDelete && (
              <button
                onClick={() => onDelete(rep.id)}
                title="Delete this agent"
                className="text-red-400/70 hover:text-red-300 transition-colors p-1 rounded-lg hover:bg-white/10"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
            <button onClick={onClose} className="text-white/40 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Stats bar */}
        <div className="grid grid-cols-3 gap-0 border-b border-gray-100 bg-gray-50 flex-shrink-0">
          {[
            { label: "Total Leads", value: leads.length, icon: BarChart3, color: "#1A6FD4" },
            { label: "Total Earned", value: fmt(totalComm), icon: DollarSign, color: "#84ab4a" },
            { label: "Pending", value: fmt(pendingComm), icon: Clock, color: "#C8920A" },
          ].map(({ label, value, icon: Icon, color }) => (
            <div key={label} className="px-5 py-4 text-center border-r border-gray-100 last:border-r-0">
              <Icon className="w-4 h-4 mx-auto mb-1" style={{ color }} />
              <div className="font-bold text-gray-900 text-sm">{value}</div>
              <div className="text-[10px] text-gray-400 uppercase tracking-wide">{label}</div>
            </div>
          ))}
        </div>

        {/* Section tabs */}
        <div className="flex border-b border-gray-100 flex-shrink-0 bg-white">
          {(["overview", "leads", "commissions", "agreement", "edit"] as const).map((s) => (
            <button
              key={s}
              onClick={() => setActiveSection(s)}
              className={`flex-1 py-3 text-xs font-semibold uppercase tracking-wide transition-colors relative ${
                activeSection === s
                  ? "text-[#84ab4a] border-b-2 border-[#84ab4a]"
                  : "text-gray-400 hover:text-gray-600"
              }`}
            >
              {s === "edit" ? "Edit Info" : s.charAt(0).toUpperCase() + s.slice(1)}
              {/* Draws the eye to an unsigned agreement without needing to
                  click in first - this is what "so hard to navigate" was
                  missing: a signal at the level admins are already scanning. */}
              {s === "agreement" && !signedAgreementQuery.isLoading && !signedAgreement?.isSigned && (
                <span className="absolute top-1.5 right-1/2 translate-x-3 w-1.5 h-1.5 rounded-full bg-amber-500" />
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">

          {/* ── OVERVIEW ── */}
          {activeSection === "overview" && (
            <div className="p-6 space-y-6">
              {/* Contact info */}
              <div>
                <div className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Contact Information</div>
                <div className="space-y-2">
                  {[
                    { icon: Mail, label: "Email", value: rep.email },
                    { icon: Phone, label: "Phone", value: rep.phone ?? "—" },
                    { icon: Award, label: "Agent Level", value: rep.agentLevel ?? "—" },
                    { icon: Layers, label: "Primary Vertical", value: rep.vertical ?? "—" },
                    { icon: User, label: "Upline Rep Code", value: rep.uplineRepCode ?? "—" },
                    { icon: User, label: "Upline Name", value: rep.uplineRepCode ? (namesQuery.data?.[rep.uplineRepCode] ?? "Loading…") : "—" },
                    { icon: Shield, label: "Agency Rep Code", value: rep.mgaRepCode ?? "—" },
                    { icon: Shield, label: "Agency Name", value: rep.mgaRepCode ? (namesQuery.data?.[rep.mgaRepCode] ?? "Loading…") : "—" },
                  ].map(({ icon: Icon, label, value }) => (
                    <div key={label} className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3">
                      <Icon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] text-gray-400 uppercase tracking-wide font-semibold">{label}</div>
                        <div className="text-sm font-medium text-gray-800 truncate">{value}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Vertical Permissions */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs font-bold text-gray-400 uppercase tracking-widest">Vertical Permissions</div>
                  {!verticalEditing ? (
                    <button
                      onClick={() => { setSelectedVerticals(allowedVerticals ?? []); setVerticalEditing(true); }}
                      className="text-xs text-[#84ab4a] hover:underline font-semibold flex items-center gap-1"
                    >
                      <Pencil className="w-3 h-3" /> Edit
                    </button>
                  ) : (
                    <div className="flex gap-2">
                      <button onClick={() => setVerticalEditing(false)} className="text-xs text-gray-400 hover:text-gray-600 font-semibold">Cancel</button>
                      <button
                        onClick={saveVerticalPermissions}
                        disabled={editRepMutation.isPending}
                        className="text-xs text-white bg-[#84ab4a] hover:bg-[#6e9038] px-3 py-1 rounded-lg font-semibold"
                      >
                        {editRepMutation.isPending ? "Saving..." : "Save"}
                      </button>
                    </div>
                  )}
                </div>
                {allowedVerticals === null && !verticalEditing && (
                  <div className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-3">
                    <strong>All verticals enabled</strong> — no restrictions set. Use Edit to restrict access.
                  </div>
                )}
                <div className="grid grid-cols-2 gap-2">
                  {ALL_VERTICALS.map(({ key, label, color, icon: Icon }) => {
                    const allowed = verticalEditing
                      ? selectedVerticals.includes(key) || selectedVerticals.length === 0
                      : isVerticalAllowed(key);
                    const checked = verticalEditing ? selectedVerticals.includes(key) : allowed;
                    return (
                      <div
                        key={key}
                        onClick={() => verticalEditing && toggleVertical(key)}
                        className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl border transition-all ${
                          verticalEditing ? "cursor-pointer" : ""
                        } ${
                          checked
                            ? "border-opacity-40 bg-opacity-5"
                            : "border-gray-100 bg-gray-50 opacity-50"
                        }`}
                        style={checked ? { borderColor: color, backgroundColor: `${color}08` } : {}}
                      >
                        <Icon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: checked ? color : "#9ca3af" }} />
                        <span className="text-xs font-semibold flex-1 truncate" style={{ color: checked ? color : "#9ca3af" }}>{label}</span>
                        {verticalEditing ? (
                          <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                            selectedVerticals.includes(key) ? "border-current" : "border-gray-300"
                          }`} style={selectedVerticals.includes(key) ? { borderColor: color, backgroundColor: color } : {}}>
                            {selectedVerticals.includes(key) && <CheckCircle2 className="w-3 h-3 text-white" />}
                          </div>
                        ) : (
                          checked
                            ? <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" style={{ color }} />
                            : <X className="w-3.5 h-3.5 text-gray-300 flex-shrink-0" />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Kickstart Provisioning */}
              <div>
                <div className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Kickstart Access</div>
                <div className="bg-[#E8620A]/5 border border-[#E8620A]/20 rounded-xl p-4">
                  <p className="text-xs text-gray-500 mb-3">
                    Provision this agent's Scale360 Kickstart account. Admin bypass — certification is not required. Their existing BC360 credentials will be used.
                  </p>
                  {kickstartSent ? (
                    <div className="flex items-center gap-2 text-sm text-green-700 font-medium">
                      <CheckCircle2 className="w-4 h-4" /> Provisioned — Kickstart will follow up.
                    </div>
                  ) : (
                    <Button
                      onClick={provisionKickstart}
                      disabled={kickstartLoading}
                      size="sm"
                      className="bg-[#E8620A] hover:bg-[#c9530a] text-white border-0 gap-1.5"
                    >
                      <Zap className="w-3.5 h-3.5" />
                      {kickstartLoading ? "Provisioning…" : "Provision Kickstart Access"}
                    </Button>
                  )}
                </div>
              </div>

              {/* Wibiz Upline Name Sync */}
              <div>
                <div className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Wibiz Sync</div>
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                  <p className="text-xs text-gray-500 mb-3">
                    Writes <code className="bg-gray-100 px-1 rounded text-[11px]">upline_name</code> to this agent's Wibiz contact record only. No other fields are changed. No tags are added.
                  </p>
                  {rep.uplineRepCode ? (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={syncUplineNameMutation.isPending}
                      onClick={() => { setUplineSyncResult(null); syncUplineNameMutation.mutate({ repCode: rep.repCode }); }}
                      className="gap-1.5 text-xs"
                    >
                      {syncUplineNameMutation.isPending ? "Syncing…" : "Sync Upline Name → Wibiz"}
                    </Button>
                  ) : (
                    <p className="text-xs text-gray-400 italic">No upline assigned — nothing to sync.</p>
                  )}
                  {uplineSyncResult && (
                    <div className="mt-3 text-[11px] font-mono bg-white border border-gray-100 rounded-lg p-3 space-y-1 text-gray-600">
                      <div><span className="text-gray-400">agentId:</span> {uplineSyncResult.agentId}</div>
                      <div><span className="text-gray-400">ghlContactId:</span> {uplineSyncResult.ghlContactId ?? "—"}</div>
                      <div><span className="text-gray-400">old upline_name:</span> {uplineSyncResult.oldUplineName ?? <em>blank</em>}</div>
                      <div><span className="text-gray-400">new upline_name:</span> {uplineSyncResult.newUplineName ?? "—"}</div>
                      <div><span className={`font-bold ${uplineSyncResult.ok ? "text-green-600" : "text-amber-600"}`}>
                        {uplineSyncResult.skipped ? `skipped — ${uplineSyncResult.reason}` : uplineSyncResult.ok ? "written ✓" : "write failed"}
                      </span></div>
                    </div>
                  )}
                </div>
              </div>

              {/* Referral links */}
              <div>
                <div className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Referral Links</div>
                <div className="space-y-2">
                  {[
                    { label: "Scale360", domain: "scale360.wibiz.ai", color: "#E8620A" },
                    { label: "Fundable360", domain: "fundable360.wibiz.ai", color: "#84ab4a" },
                    { label: "Legacy360", domain: "legacy360.wibiz.ai", color: "#C8920A" },
                    { label: "Share360 (Business)", domain: "share360.wibiz.ai", color: "#1A6FD4" },
                    { label: "Share360 (Nonprofit)", domain: "share360.wibiz.ai/nonprofit", color: "#0FA8A8" },
                  ].map(({ label, domain, color }) => {
                    const link = `https://${domain}/?ref=${rep.repCode}`;
                    return (
                      <div key={label} className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-2.5">
                        <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: color }} />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-bold" style={{ color }}>{label}</div>
                          <div className="text-[10px] text-gray-400 font-mono truncate">{link}</div>
                        </div>
                        <button
                          onClick={() => { navigator.clipboard.writeText(link); toast.success(`${label} link copied!`); }}
                          className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-lg hover:bg-gray-200"
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                        <a href={link} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-lg hover:bg-gray-200">
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* ── LEADS ── */}
          {activeSection === "leads" && (
            <div className="p-6">
              <div className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">
                Leads ({leads.length})
              </div>
              {leads.length === 0 ? (
                <div className="py-12 text-center text-gray-400">
                  <BarChart3 className="w-8 h-8 mx-auto mb-3 opacity-30" />
                  <p className="font-medium text-sm">No leads yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {leads.map((lead: any) => (
                    <div key={lead.id} className="bg-gray-50 rounded-xl px-4 py-3 flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-[#84ab4a]/10 flex items-center justify-center flex-shrink-0">
                        <User className="w-4 h-4 text-[#84ab4a]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm text-gray-900 truncate">{lead.firstName} {lead.lastName}</div>
                        <div className="text-xs text-gray-400 truncate">{lead.email}</div>
                      </div>
                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        {lead.verticalName && (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{
                            background: `${STAGE_COLORS[lead.stage] ?? "#84ab4a"}15`,
                            color: STAGE_COLORS[lead.stage] ?? "#84ab4a"
                          }}>
                            {lead.verticalName}
                          </span>
                        )}
                        <span className="text-[10px] text-gray-400">
                          {STAGE_LABELS[lead.stage] ?? lead.stage}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── COMMISSIONS ── */}
          {activeSection === "commissions" && (
            <div className="p-6">
              <div className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">
                Commissions ({commissions.length})
              </div>
              {commissions.length === 0 ? (
                <div className="py-12 text-center text-gray-400">
                  <DollarSign className="w-8 h-8 mx-auto mb-3 opacity-30" />
                  <p className="font-medium text-sm">No commissions yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {commissions.map((c: any) => (
                    <div key={c.id} className="bg-gray-50 rounded-xl px-4 py-3 flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm text-gray-900">
                          {c.isManualCommission ? "Manual commission" : (c.clientName ?? `Lead #${c.leadId}`)}
                        </div>
                        <div className="text-xs text-gray-400">
                          {c.verticalName ?? "-"} · {c.isManualCommission ? c.notes : (c.type ?? "enrollment")}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        <span className="font-bold text-sm text-[#84ab4a]">{fmt(Number(c.amount ?? 0))}</span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                          c.status === "paid" ? "bg-green-100 text-green-700"
                          : c.status === "pending" ? "bg-amber-100 text-amber-700"
                          : "bg-gray-100 text-gray-500"
                        }`}>{c.status}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── AGREEMENT ── */}
          {/* Its own top-level tab, not a line item buried in Overview's
              scroll - the direct fix for "the download and view button
              should be found somewhere, it's so hard to navigate". Header
              status pill and the tab's amber dot both jump straight here. */}
          {activeSection === "agreement" && (
            <div className="p-6">
              <div className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">
                Platform Participation Agreement
              </div>

              <RecordAgreementHistory kind="rep" id={rep.id} />

              {signedAgreementQuery.isLoading ? (
                <div className="text-sm text-gray-400">Loading…</div>
              ) : !signedAgreement ? (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="text-sm font-semibold text-amber-800">No acceptance on file</div>
                    <div className="text-xs text-amber-700 mt-1">
                      This associate has not accepted or signed the Platform Participation Agreement yet.
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  className={`rounded-xl border p-5 ${
                    signedAgreement.isSigned
                      ? "bg-green-50 border-green-200"
                      : "bg-amber-50 border-amber-200"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-4">
                    {signedAgreement.isSigned ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
                    )}
                    <div className={`text-sm font-semibold ${signedAgreement.isSigned ? "text-green-800" : "text-amber-800"}`}>
                      {signedAgreement.isSigned ? "Signed" : "Accepted, unsigned (legacy)"}
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4 mb-5">
                    <div className="min-w-0">
                      <div className="text-[10px] text-gray-500 uppercase tracking-wide font-semibold">Signed by</div>
                      <div className="text-sm font-medium text-gray-900 mt-0.5 truncate" title={signedAgreement.printedName || undefined}>
                        {signedAgreement.printedName || <span className="text-gray-400">Not recorded</span>}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] text-gray-500 uppercase tracking-wide font-semibold">Date</div>
                      <div className="text-sm font-medium text-gray-900 mt-0.5">
                        {new Date(signedAgreement.acceptedAt).toLocaleDateString("en-US", {
                          year: "numeric", month: "short", day: "numeric",
                        })}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] text-gray-500 uppercase tracking-wide font-semibold">Issue</div>
                      <div className="text-sm font-medium text-gray-900 mt-0.5">v{signedAgreement.agreementVersion}</div>
                    </div>
                  </div>

                  {signedAgreement.isSigned && signedAgreement.signedDocKey ? (
                    <div className="flex items-center gap-3">
                      <a
                        href={`/api/files/${signedAgreement.signedDocKey}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 bg-[#1A6FD4] hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors"
                      >
                        <ExternalLink className="w-4 h-4" /> View signed agreement
                      </a>
                      <a
                        href={`/api/files/${signedAgreement.signedDocKey}?download=1`}
                        className="flex items-center gap-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors"
                      >
                        <Download className="w-4 h-4" /> Download
                      </a>
                    </div>
                  ) : (
                    <div className="text-xs text-amber-700">
                      Accepted by checkbox before signed agreements were introduced. No signed copy exists for this associate.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── EDIT INFO ── */}
          {activeSection === "edit" && (
            <div className="p-6 space-y-4">
              <div className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">Edit Agent Information</div>
              {!isEditing ? (
                <Button
                  onClick={startEdit}
                  className="w-full bg-[#84ab4a] hover:bg-[#6e9038] text-white border-0"
                >
                  <Pencil className="w-4 h-4 mr-2" /> Edit Agent Info
                </Button>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 block">First Name</label>
                      <Input value={editForm.firstName ?? ""} onChange={e => setEditForm(f => ({ ...f, firstName: e.target.value }))} />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 block">Last Name</label>
                      <Input value={editForm.lastName ?? ""} onChange={e => setEditForm(f => ({ ...f, lastName: e.target.value }))} />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 block">Email</label>
                    <Input type="email" value={editForm.email ?? ""} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 block">Phone</label>
                    <Input value={editForm.phone ?? ""} onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 block">Agent Level</label>
                    <Select value={editForm.agentLevel ?? "Associate"} onValueChange={v => setEditForm(f => ({ ...f, agentLevel: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Super Agency">Super Agency</SelectItem>
                        <SelectItem value="Super Team">Super Team</SelectItem>
                        <SelectItem value="Agency">Agency</SelectItem>
                        <SelectItem value="Senior Associate">Senior Associate</SelectItem>
                        <SelectItem value="Associate">Associate</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 block">Primary Vertical</label>
                    <Select value={editForm.vertical ?? ""} onValueChange={v => setEditForm(f => ({ ...f, vertical: v }))}>
                      <SelectTrigger><SelectValue placeholder="Select vertical..." /></SelectTrigger>
                      <SelectContent>
                        {ALL_VERTICALS.map(v => <SelectItem key={v.key} value={v.key}>{v.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 block">Upline Rep Code</label>
                      <Input value={editForm.uplineRepCode ?? ""} onChange={e => setEditForm(f => ({ ...f, uplineRepCode: e.target.value }))} placeholder="e.g. BC-360" />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 block">Agency Rep Code</label>
                      <Input value={editForm.mgaRepCode ?? ""} onChange={e => setEditForm(f => ({ ...f, mgaRepCode: e.target.value }))} placeholder="e.g. BC-360" />
                    </div>
                  </div>

                  {/* Vertical Permissions in edit */}
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">Allowed Verticals</label>
                    <p className="text-xs text-gray-400 mb-3">Leave all unchecked to allow all verticals. Check specific ones to restrict access.</p>
                    <div className="grid grid-cols-2 gap-2">
                      {ALL_VERTICALS.map(({ key, label, color, icon: Icon }) => (
                        <div
                          key={key}
                          onClick={() => toggleVertical(key)}
                          className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border cursor-pointer transition-all ${
                            selectedVerticals.includes(key)
                              ? "border-opacity-40"
                              : "border-gray-200 bg-gray-50"
                          }`}
                          style={selectedVerticals.includes(key) ? { borderColor: color, backgroundColor: `${color}08` } : {}}
                        >
                          <Icon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: selectedVerticals.includes(key) ? color : "#9ca3af" }} />
                          <span className="text-xs font-semibold flex-1" style={{ color: selectedVerticals.includes(key) ? color : "#9ca3af" }}>{label}</span>
                          <div
                            className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                              selectedVerticals.includes(key) ? "" : "border-gray-300"
                            }`}
                            style={selectedVerticals.includes(key) ? { borderColor: color, backgroundColor: color } : {}}
                          >
                            {selectedVerticals.includes(key) && <CheckCircle2 className="w-3 h-3 text-white" />}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-3 pt-2">
                    <Button variant="outline" onClick={() => setIsEditing(false)} className="flex-1">Cancel</Button>
                    <Button
                      onClick={saveEdit}
                      disabled={editRepMutation.isPending}
                      className="flex-1 bg-[#84ab4a] hover:bg-[#6e9038] text-white border-0"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {editRepMutation.isPending ? "Saving..." : "Save Changes"}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
